<template>
  <Toast ref="toast" />
  <div class="analytics">
    <!-- ================= FILTERS ================= -->

    <div class="filters">
      <div class="filter-block">
        <label>Надстройка (Prefix)</label>

        <MultiSelect
          v-model="filters.prefixDefinitionIds"
          :options="prefixOptions"
          optionLabel="label"
          optionValue="value"
          display="chip"
          filter
        />

        <div class="quality-buttons">
          <button
            v-for="q in qualityOptions"
            :key="'p-' + q.value"
            :style="{ backgroundColor: q.color }"
            :class="{ active: filters.prefixQualities.includes(q.value) }"
            @click="toggleQuality('prefixQualities', q.value)"
          />
        </div>
      </div>

      <div class="filter-block">
        <label>Отклонение (Suffix)</label>

        <MultiSelect
          v-model="filters.suffixDefinitionIds"
          :options="suffixOptions"
          optionLabel="label"
          optionValue="value"
          display="chip"
          filter
        />

        <div class="quality-buttons">
          <button
            v-for="q in qualityOptions"
            :key="'s-' + q.value"
            :style="{ backgroundColor: q.color }"
            :class="{ active: filters.suffixQualities.includes(q.value) }"
            @click="toggleQuality('suffixQualities', q.value)"
          />
        </div>
      </div>

      <div class="filter-block">
        <label>Концепт (Affix)</label>

        <MultiSelect
          v-model="filters.affixDefinitionIds"
          :options="affixOptions"
          optionLabel="label"
          optionValue="value"
          display="chip"
          filter
        />

        <div class="quality-buttons">
          <button
            v-for="q in qualityOptions"
            :key="'a-' + q.value"
            :style="{ backgroundColor: q.color, color: q.color }"
            :class="{ active: filters.affixQualities.includes(q.value) }"
            @click="toggleQuality('affixQualities', q.value)"
          />
        </div>
      </div>
      <div class="filter-block">
        <label>Быстрый поиск по названиям</label>
        <div class="search-input-group">
          <input
            v-model="attributeInput"
            placeholder="Например: Вертикальный или Вертикальный Дрожащий"
            class="attr-input"
            @keyup.enter="handleSearch"
          />
          <button @click="handleSearch" class="search-button">Поиск</button>
        </div>
        <div class="parse-status" :class="parseStatusType" v-if="parseStatus">
          {{ parseStatus }}
        </div>
      </div>
    </div>

    <!-- ================= CHART ================= -->

    <div class="chart-wrapper">
      <Line :data="chartData" :options="options" />
    </div>

    <!-- ================= TABLE ================= -->

    <!-- Статистика -->
    <div class="stats-section" v-if="sortedModules.length > 0">
      <div class="stat-card">
        <h3>Статистика по результатам</h3>
        <div class="stat-item">
          <span class="stat-label">Всего найдено:</span>
          <span class="stat-value">{{ sortedModules.length }}</span>
        </div>
        <div class="stat-item">
          <span class="stat-label">Средняя цена:</span>
          <span class="stat-value">{{ formatPrice(averagePrice) }}</span>
        </div>
      </div>
    </div>

    <div class="attributes-table">
      <table>
        <thead>
          <tr>
            <th>Время</th>
            <th>Цена</th>
            <th>Надстройка</th>
            <th>Отклонение</th>
            <th>Концепт</th>
          </tr>
        </thead>

        <tbody>
          <tr
            v-for="m in paginatedModules"
            :key="m.id"
            class="clickable-row"
            @click="copyToClipboard(getAllAttributesText(m))"
          >
            <td>{{ formatDate(m.time) }}</td>
            <td>{{ formatPrice(m.price) }}</td>
            <td v-for="attr in m.attributes" :key="attr.id">
              <div
                class="attribute-item"
                :style="{
                  color: getQuality(attr.quality)?.borderColor
                }"
              >
                {{ getStatName(attr.definitionId, attr.slot)?.nameRu }}
                ({{ getStatName(attr.definitionId, attr.slot)?.parameterRu }})
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Пагинация -->
    <div class="pagination-wrapper" v-if="sortedModules.length > rowsPerPage">
      <Paginator
        :rows="rowsPerPage"
        :totalRecords="sortedModules.length"
        :first="first"
        @page="onPageChange"
        :rowsPerPageOptions="[10, 25, 50, 100]"
        currentPageReportTemplate="Показано {first} - {last} из {totalRecords}"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, onBeforeUnmount, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { Line } from 'vue-chartjs'
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Tooltip,
  Legend,
  LineController,
  Filler
} from 'chart.js'

ChartJS.register(
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Tooltip,
  Legend,
  LineController,
  Filler
)

import MultiSelect from 'primevue/multiselect'
import Paginator from 'primevue/paginator'
import Toast from 'primevue/toast'
import { copyText } from 'vue3-clipboard'
import { fetchModules } from '@/api/http'
import { MODULE_STATS, QUALITY_LIST } from '@/components/module/data'
import { formatPrice } from '@/common/function'

/* ================= VARIABLES ================= */

const attributeInput = ref('')
const parseStatus = ref('')
const parseStatusType = ref('')
const parseCache = ref(new Map())
const toast = ref()

/* ================= ROUTER ================= */

const route = useRoute()
const router = useRouter()

/* ================= QUERY SYNC ================= */

const parseQueryFilters = () => {
  const query = route.query

  const newFilters = {
    prefixDefinitionIds: query.prefix ? String(query.prefix).split(',').filter(Boolean) : [],
    suffixDefinitionIds: query.suffix ? String(query.suffix).split(',').filter(Boolean) : [],
    affixDefinitionIds: query.affix ? String(query.affix).split(',').filter(Boolean) : [],
    prefixQualities: query.prefixQualities
      ? String(query.prefixQualities).split(',').filter(Boolean).map(Number)
      : [],
    suffixQualities: query.suffixQualities
      ? String(query.suffixQualities).split(',').filter(Boolean).map(Number)
      : [],
    affixQualities: query.affixQualities
      ? String(query.affixQualities).split(',').filter(Boolean).map(Number)
      : []
  }

  Object.assign(filters, newFilters)
}

const updateQuery = () => {
  const query = {}

  if (filters.prefixDefinitionIds.length > 0) {
    query.prefix = filters.prefixDefinitionIds.join(',')
  }

  if (filters.suffixDefinitionIds.length > 0) {
    query.suffix = filters.suffixDefinitionIds.join(',')
  }

  if (filters.affixDefinitionIds.length > 0) {
    query.affix = filters.affixDefinitionIds.join(',')
  }

  if (filters.prefixQualities.length > 0) {
    query.prefixQualities = filters.prefixQualities.join(',')
  }

  if (filters.suffixQualities.length > 0) {
    query.suffixQualities = filters.suffixQualities.join(',')
  }

  if (filters.affixQualities.length > 0) {
    query.affixQualities = filters.affixQualities.join(',')
  }

  router.replace({ query })
}

/* ================= FILTERS ================= */

const filters = reactive({
  prefixDefinitionIds: [],
  suffixDefinitionIds: [],
  affixDefinitionIds: [],
  prefixQualities: [],
  suffixQualities: [],
  affixQualities: []
})

const qualityOptions = QUALITY_LIST

const toggleQuality = (key, value) => {
  const arr = filters[key]
  const i = arr.indexOf(value)
  i > -1 ? arr.splice(i, 1) : arr.push(value)
}

/* ================= OPTIONS ================= */

const prefixOptions = computed(() =>
  MODULE_STATS.filter(m => m.slots.includes('prefix')).map(m => ({
    label: m.nameRu,
    value: m.id
  }))
)

const suffixOptions = computed(() =>
  MODULE_STATS.filter(m => m.slots.includes('suffix')).map(m => ({
    label: m.nameRu,
    value: m.id
  }))
)

const affixOptions = computed(() =>
  MODULE_STATS.filter(m => m.slots.includes('affix')).map(m => ({
    label: m.nameRu,
    value: m.id
  }))
)

/* ================= DATA ================= */

const modules = ref([])
let loadRequestId = 0
let loadTimer = null

const load = async () => {
  const requestId = ++loadRequestId
  const result = await fetchModules({
    prefixDefinitionIds: filters.prefixDefinitionIds,
    suffixDefinitionIds: filters.suffixDefinitionIds,
    affixDefinitionIds: filters.affixDefinitionIds,
    prefixQlts: filters.prefixQualities,
    suffixQlts: filters.suffixQualities,
    affixQlts: filters.affixQualities
  })

  if (requestId === loadRequestId) {
    modules.value = result
  }
}

const scheduleLoad = () => {
  if (loadTimer) {
    window.clearTimeout(loadTimer)
  }

  loadTimer = window.setTimeout(() => {
    loadTimer = null
    load()
  }, 250)
}

onMounted(() => {
  parseQueryFilters()
  load()
})

onBeforeUnmount(() => {
  if (loadTimer) {
    window.clearTimeout(loadTimer)
  }
})

watch(
  filters,
  () => {
    first.value = 0
    scheduleLoad()
    updateQuery()
  },
  { deep: true }
)

/* ================= PARSING ================= */

const handleSearch = () => {
  const input = attributeInput.value.trim()

  if (!input) {
    parseStatus.value = ''
    return
  }

  if (parseCache.value.has(input)) {
    const cached = parseCache.value.get(input)
    parseStatus.value = cached.status
    parseStatusType.value = cached.type

    if (cached.filters) {
      filters.prefixDefinitionIds = cached.filters.prefixDefinitionIds
      filters.suffixDefinitionIds = cached.filters.suffixDefinitionIds
      filters.affixDefinitionIds = cached.filters.affixDefinitionIds
    }
    return
  }

  // Улучшенный парсинг с поддержкой кавычек
  const words = parseWordsWithQuotes(input)

  if (words.length === 0) {
    parseStatus.value = 'Введите хотя бы один атрибут'
    parseStatusType.value = 'error'
    return
  }

  const newFilters = {
    prefixDefinitionIds: [],
    suffixDefinitionIds: [],
    affixDefinitionIds: []
  }

  const found = []
  const notFound = []

  words.forEach(wordName => {
    const searchWord = wordName.toLowerCase()

    // Более гибкий поиск - ищем частичные совпадения и точные
    const prefix = MODULE_STATS.find(m => {
      if (!m.slots.includes('prefix')) return false
      const nameMatch = m.nameRu.toLowerCase() === searchWord
      const partialMatch =
        m.nameRu.toLowerCase().includes(searchWord) || searchWord.includes(m.nameRu.toLowerCase())
      return nameMatch || partialMatch
    })

    const suffix = MODULE_STATS.find(m => {
      if (!m.slots.includes('suffix')) return false
      const nameMatch = m.nameRu.toLowerCase() === searchWord
      const partialMatch =
        m.nameRu.toLowerCase().includes(searchWord) || searchWord.includes(m.nameRu.toLowerCase())
      return nameMatch || partialMatch
    })

    const affix = MODULE_STATS.find(m => {
      if (!m.slots.includes('affix')) return false
      const nameMatch = m.nameRu.toLowerCase() === searchWord
      const partialMatch =
        m.nameRu.toLowerCase().includes(searchWord) || searchWord.includes(m.nameRu.toLowerCase())
      return nameMatch || partialMatch
    })

    if (prefix) {
      newFilters.prefixDefinitionIds.push(prefix.id)
      found.push(`${wordName} (префикс: ${prefix.nameRu})`)
    } else if (suffix) {
      newFilters.suffixDefinitionIds.push(suffix.id)
      found.push(`${wordName} (суффикс: ${suffix.nameRu})`)
    } else if (affix) {
      newFilters.affixDefinitionIds.push(affix.id)
      found.push(`${wordName} (аффикс: ${affix.nameRu})`)
    } else {
      notFound.push(wordName)
    }
  })

  filters.prefixDefinitionIds = newFilters.prefixDefinitionIds
  filters.suffixDefinitionIds = newFilters.suffixDefinitionIds
  filters.affixDefinitionIds = newFilters.affixDefinitionIds

  if (found.length === words.length) {
    parseStatus.value = `✓ Найдены все атрибуты (${found.length})`
    parseStatusType.value = 'success'
  } else if (found.length > 0) {
    parseStatus.value = `Найдено: ${found.join(', ')}. Не найдено: ${notFound.join(', ')}`
    parseStatusType.value = 'warning'
  } else {
    // Добавляем подсказку с доступными атрибутами
    const availableAffixes = MODULE_STATS.filter(m => m.slots.includes('affix'))
      .map(m => m.nameRu)
      .slice(0, 10)
      .join(', ')

    parseStatus.value = `Не найдено атрибутов: ${notFound.join(
      ', '
    )}. Доступные аффиксы: ${availableAffixes}...`
    parseStatusType.value = 'error'
  }

  parseCache.value.set(input, {
    status: parseStatus.value,
    type: parseStatusType.value,
    filters: newFilters
  })

  if (parseCache.value.size > 100) {
    const firstKey = parseCache.value.keys().next().value
    parseCache.value.delete(firstKey)
  }
}

/* Парсинг слов с поддержкой кавычек */
const parseWordsWithQuotes = input => {
  const words = []
  let current = ''
  let inQuotes = false
  let quoteChar = ''

  for (let i = 0; i < input.length; i++) {
    const char = input[i]

    // Начало кавычек
    if (!inQuotes && (char === '"' || char === "'")) {
      if (current.trim()) {
        words.push(current.trim())
        current = ''
      }
      inQuotes = true
      quoteChar = char
      continue
    }

    // Конец кавычек
    if (inQuotes && char === quoteChar) {
      if (current.trim()) {
        words.push(current.trim())
      }
      current = ''
      inQuotes = false
      quoteChar = ''
      continue
    }

    // Разделитель слов вне кавычек
    if (!inQuotes && char === ' ') {
      if (current.trim()) {
        words.push(current.trim())
        current = ''
      }
      continue
    }

    current += char
  }

  // Добавляем последнее слово
  if (current.trim()) {
    words.push(current.trim())
  }

  return words.filter(word => word.length > 0)
}

/* ================= PAGINATION ================= */

const first = ref(0)
const rowsPerPage = ref(25)

const sortedModules = computed(() => modules.value)

const paginatedModules = computed(() => {
  const startIndex = first.value
  const endIndex = startIndex + rowsPerPage.value
  return sortedModules.value.slice(startIndex, endIndex)
})

const averagePrice = computed(() => {
  if (!sortedModules.value.length) return 0

  const validPrices = sortedModules.value
    .map(m => {
      const price = Number(m.price) || 0
      return price
    })
    .filter(price => price > 0 && price < 100000000)

  if (!validPrices.length) return 0

  const total = validPrices.reduce((sum, price) => sum + price, 0)
  const average = total / validPrices.length

  return Math.round(average)
})

const onPageChange = event => {
  first.value = event.first
}

/* ================= HELPERS ================= */

const getStatName = (definitionId, slot) => {
  if (!definitionId || !slot) return { nameRu: '', parameterRu: '' }

  const cleaned = definitionId?.replace(/_(pre|suf|aff)$/g, '').replace(/_(pre|suf|aff)_?/g, '')

  const stat = MODULE_STATS.find(s => s && s.id === cleaned && s.slots?.includes(slot))

  return stat || { nameRu: definitionId, parameterRu: '' }
}

const getAllAttributesText = module => {
  if (!module || !module.attributes) return ''

  const prefix = module.attributes.find(attr => attr.slot === 'prefix')
  const suffix = module.attributes.find(attr => attr.slot === 'suffix')
  const affix = module.attributes.find(attr => attr.slot === 'affix')

  const prefixName = prefix ? getStatName(prefix.definitionId, prefix.slot)?.nameRu || '' : ''
  const suffixName = suffix ? getStatName(suffix.definitionId, suffix.slot)?.nameRu || '' : ''
  const affixName = affix ? getStatName(affix.definitionId, affix.slot)?.nameRu || '' : ''

  return `${prefixName} ${suffixName} ${affixName}`.trim()
}

const getQuality = quality => {
  return QUALITY_LIST.find(q => q.value === quality)
}

const formatDate = date => {
  return new Date(date).toLocaleString()
}

/* ================= COPY ================= */

const copyToClipboard = text => {
  toast.value?.add({
    severity: 'info',
    summary: 'Скопировано',
    detail: text,
    life: 2000
  })

  copyText(text, undefined, error => {
    if (error) console.error('Copy error:', error)
  })
}

/* ================= CHART ================= */

const chartData = computed(() => {
  const labels = sortedModules.value.map(m => formatDate(m.time))
  const data = sortedModules.value.map(m => Number(m.price))

  return {
    labels,
    datasets: [
      {
        label: 'Aggregated Price',
        data,
        borderColor: '#60a5fa',
        backgroundColor: '#60a5fa',
        pointBackgroundColor: '#60a5fa',
        pointBorderColor: '#111827',
        borderWidth: 2,
        pointRadius: 5,
        tension: 0.25,
        spanGaps: true
      }
    ]
  }
})

const options = {
  responsive: true,
  maintainAspectRatio: false,

  plugins: {
    legend: {
      position: 'top',
      labels: {
        color: '#e5e7eb',
        boxWidth: 12,
        padding: 12
      }
    },

    tooltip: {
      enabled: false,
      external: context => {
        const tooltip = context.tooltip
        const chart = context.chart

        let tooltipEl = document.getElementById('chart-tooltip')

        if (!tooltipEl) {
          tooltipEl = document.createElement('div')
          tooltipEl.id = 'chart-tooltip'
          tooltipEl.style.position = 'absolute'
          tooltipEl.style.background = '#1f2937'
          tooltipEl.style.padding = '12px'
          tooltipEl.style.borderRadius = '8px'
          tooltipEl.style.color = 'white'
          tooltipEl.style.fontSize = '13px'
          tooltipEl.style.pointerEvents = 'none'
          tooltipEl.style.boxShadow = '0 10px 25px rgba(0,0,0,0.4)'
          document.body.appendChild(tooltipEl)
        }

        if (tooltip.opacity === 0) {
          tooltipEl.style.opacity = 0
          return
        }

        const index = tooltip.dataPoints?.[0]?.dataIndex
        const module = sortedModules.value[index]

        if (!module) return

        const resultTime = formatDate(module.time)
        const resultPrice = formatPrice(module.price)

        let html = `
      <div style="font-weight:bold;margin-bottom:6px;color: gray">
        ${resultTime}
      </div>
      <div style="font-weight:bold;margin-bottom:6px;">
        ${resultPrice}
      </div>
    `

        module.attributes?.slice(0, 3).forEach(attr => {
          const stat = getStatName(attr.definitionId, attr.slot)
          const quality = QUALITY_LIST.find(q => q.value === attr.quality)

          html += `
        <div style="color:${quality?.color || '#ffffff'};margin-bottom:4px;">
          ${stat?.nameRu || attr.definitionId} (${stat?.parameterRu})
        </div>
      `
        })

        tooltipEl.innerHTML = html

        const { caretX, caretY } = tooltip

        const rect = chart.canvas.getBoundingClientRect()

        tooltipEl.style.opacity = 1
        tooltipEl.style.left = rect.left + window.scrollX + caretX + 'px'
        tooltipEl.style.top = rect.top + window.scrollY + caretY - 120 + 'px'
      }
    }
  },

  scales: {
    x: {
      ticks: { color: '#9ca3af' },
      grid: { color: 'rgba(255,255,255,0.05)' }
    },
    y: {
      beginAtZero: false,
      ticks: { color: '#9ca3af' },
      grid: { color: 'rgba(255,255,255,0.05)' }
    }
  }
}
</script>

<style scoped>
.analytics {
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 20px;
  background: #111827;
  border-radius: 12px;
}

/* FILTERS */
.filters {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 16px;
}

.filter-block {
  display: flex;
  flex-direction: column;
  gap: 10px;
  background: #1f2937;
  padding: 16px;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

/* QUALITY */
.quality-buttons {
  display: flex;
  gap: 6px;
}

.quality-buttons button {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.2);
  cursor: pointer;
  transition: transform 0.15s ease;
}

.quality-buttons button.active {
  transform: scale(1.25);
  border: 1px solid white;
}

/* SEARCH */
.search-input-group {
  display: flex;
  gap: 8px;
}

.attr-input {
  background: #111;
  border: 1px solid #333;
  padding: 8px 12px;
  color: white;
  border-radius: 6px;
  width: 100%;
  font-size: 14px;
}

.attr-input:focus {
  outline: none;
  border-color: #60a5fa;
}

.search-button {
  background: #60a5fa;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: background-color 0.2s ease;
  white-space: nowrap;
}

.search-button:hover {
  background: #3b82f6;
}

.search-button:active {
  background: #2563eb;
}

.parse-status {
  margin-top: 6px;
  padding: 6px 8px;
  border-radius: 4px;
  font-size: 12px;
}

.parse-status.success {
  background: rgba(74, 222, 128, 0.1);
  color: #4ade80;
  border: 1px solid rgba(74, 222, 128, 0.3);
}

.parse-status.warning {
  background: rgba(251, 191, 36, 0.1);
  color: #fbbf24;
  border: 1px solid rgba(251, 191, 36, 0.3);
}

.parse-status.error {
  background: rgba(248, 113, 113, 0.1);
  color: #f87171;
  border: 1px solid rgba(248, 113, 113, 0.3);
}

/* STATS */
.stats-section {
  margin-bottom: 24px;
}

.stat-card {
  background: #1f2937;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.stat-card h3 {
  margin: 0 0 12px 0;
  color: #e5e7eb;
  font-size: 16px;
  font-weight: 600;
}

.stat-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.stat-item:last-child {
  border-bottom: none;
}

.stat-label {
  color: #9ca3af;
  font-size: 14px;
}

.stat-value {
  color: #60a5fa;
  font-weight: 600;
  font-size: 14px;
}

/* CHART */
.chart-wrapper {
  height: 400px;
  background: #1f2937;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

/* TABLE */
.attributes-table {
  background: #1f2937;
  border-radius: 12px;
  padding: 12px;
  overflow-x: auto;
  color: #e5e7eb;
}

.attributes-table table {
  width: 100%;
  border-collapse: collapse;
  font-size: 16px;
}

.attributes-table th {
  text-align: left;
  padding: 8px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.attributes-table td {
  padding: 8px;
  vertical-align: top;
}

.attribute-item {
  display: inline-block;
}

.clickable-row {
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.clickable-row:hover {
  background-color: rgba(96, 165, 250, 0.1);
}

/* PAGINATION */
.pagination-wrapper {
  margin-top: 20px;
  display: flex;
  justify-content: center;
}

.p-paginator {
  background: #1f2937;
  border: 1px solid #374151;
  border-radius: 8px;
}

.p-paginator .p-paginator-pages .p-paginator-page {
  background: #374151;
  color: #e5e7eb;
  border: 1px solid #4b5563;
}

.p-paginator .p-paginator-pages .p-paginator-page.p-highlight {
  background: #60a5fa;
  color: white;
  border-color: #60a5fa;
}
</style>
